import { RaRecord, GetListParams, GetOneParams, CreateParams, UpdateParams, UpdateManyParams, DeleteParams, DeleteManyParams, GetManyParams, GetManyReferenceParams, QueryFunctionContext } from 'ra-core';
import { API_URL, httpClient } from './httpClient';
import { customDataProviderMethods } from './services/api.service';

/**
 * Sort direction enum (matches backend SortDir)
 */
enum SortDir {
  ASC = 'ASC',
  DESC = 'DESC',
}

/**
 * Virtual resource aliases - maps FE-only resource names to real API resources
 * with extra filters/data injected automatically.
 *
 * This allows domain-specific tag resources (ticket-tag, conversation-tag, etc.)
 * to all hit the same `tag` backend API while scoping by domain.
 */
interface ResourceAlias {
  /** The real API resource name */
  apiResource: string;
  /** Extra filters injected into list/search/reference requests */
  listFilters?: Record<string, unknown>;
  /** Extra data injected into create requests */
  createData?: Record<string, unknown>;
}

const resourceAliases: Record<string, ResourceAlias> = {
  'ticket-tag': {
    apiResource: 'tag',
    listFilters: { domain_eq: 'ticket' },
    createData: { domain: 'ticket' },
  },
  'conversation-tag': {
    apiResource: 'tag',
    listFilters: { domain_eq: 'conversation' },
    createData: { domain: 'conversation' },
  },
  'job-application-tag': {
    apiResource: 'tag',
    listFilters: { domain_eq: 'job_application' },
    createData: { domain: 'job_application' },
  },
};

function resolveResource(resource: string): { apiResource: string; alias?: ResourceAlias } {
  const alias = resourceAliases[resource];
  return { apiResource: alias?.apiResource ?? resource, alias };
}

/**
 * Backend request body for list/search/reference endpoints
 */
interface BackendListRequest {
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: SortDir;
  [key: string]: unknown;
}

/**
 * Backend response format
 */
interface BackendListResponse<T = unknown> {
  data: T[];
  totalCount: number;
}

/**
 * Backend response format for single item
 */
interface BackendResponse<T = unknown> {
  data: T;
}

// Removed CustomDataProvider - using DataProvider directly

/**
 * Maps React Admin filter format to our backend filter format
 * 
 * Supports StackedFilters format (React Admin Enterprise) and simple format:
 * 
 * 1. StackedFilters format (preferred):
 *    - { name_eq: 'value', views_gt: 100 } → Pass through directly
 *    - { statusId_in: [1, 2, 3] } → Pass through directly
 * 
 * 2. Simple React Admin format (fallback):
 *    - { field: 'value' } → field_q (search/contains)
 *    - { field: [1, 2, 3] } → field_in (as array)
 * 
 * Our backend format:
 * - field_q: Search/contains
 * - field_eq: Exact match
 * - field_ne: Not equal
 * - field_gt/gte/lt/lte: Comparisons
 * - field_in: Array of values
 * 
 * Supported operators: q, eq, ne, gt, gte, lt, lte, in
 */
function mapReactAdminFiltersToBackend(filters: Record<string, unknown>): Record<string, unknown> {
    const backendFilters: Record<string, unknown> = {};
    
    // Valid operators that StackedFilters or direct filters might use
    const validOperators = ['q', 'eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'in', 'null', 'notnull'];
    
    // Helper to check if a key already contains an operator
    const hasOperator = (key: string): boolean => {
        const parts = key.split('_');
        if (parts.length < 2) return false;
        const operator = parts[parts.length - 1];
        return validOperators.includes(operator);
    };

    // Helper to format date with time component and convert to UTC ISO string
    const formatDateWithTime = (dateValue: string, operator: string): string => {
        // Check if it's a date string in YYYY-MM-DD format (date only)
        const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (dateOnlyRegex.test(dateValue)) {
            // Create date in local timezone, then convert to UTC ISO string
            if (operator === 'gte') {
                // Start of day in local timezone
                const date = new Date(`${dateValue}T00:00:00`);
                return date.toISOString();
            } else if (operator === 'lte') {
                // End of day in local timezone
                const date = new Date(`${dateValue}T23:59:59.999`);
                return date.toISOString();
            }
            return new Date(dateValue).toISOString();
        }
        
        // Handle datetime-local format (YYYY-MM-DDTHH:mm) - add seconds if missing
        const datetimeLocalRegex = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})$/;
        const datetimeLocalMatch = dateValue.match(datetimeLocalRegex);
        if (datetimeLocalMatch) {
            const baseDateTime = datetimeLocalMatch[1];
            // Add seconds based on operator and convert to UTC
            if (operator === 'gte') {
                return new Date(`${baseDateTime}:00`).toISOString();
            } else if (operator === 'lte') {
                return new Date(`${baseDateTime}:59.999`).toISOString();
            }
            return new Date(`${baseDateTime}:00`).toISOString();
        }
        
        // If it's already a full datetime string, ensure it's in ISO format
        // This handles cases where the value might already be a Date string
        const parsedDate = new Date(dateValue);
        if (!isNaN(parsedDate.getTime())) {
            return parsedDate.toISOString();
        }
        
        return dateValue;
    };

    for (const [key, value] of Object.entries(filters)) {
        // Special handling for null/notnull operators - they don't need a value
        // Check this FIRST before skipping empty values
        const parts = key.split('_');
        const operator = parts.length >= 2 ? parts[parts.length - 1] : '';
        if (operator === 'null' || operator === 'notnull') {
            // For null/not_null operators, pass through the filter key as-is
            // The backend will handle these special operators
            // Accept any truthy value (true, 1, 'true', etc.) or use true as default
            // Even if value is empty string, we still want to apply the filter
            backendFilters[key] = (value !== undefined && value !== null && value !== '') ? value : true;
            continue;
        }

        // For other operators, skip if value is empty
        if (value === undefined || value === null || value === '') continue;
        if (Array.isArray(value) && value.length === 0) continue;

        // If filter already has an operator (StackedFilters format), pass through directly
        if (hasOperator(key)) {
            // Format date filters with time components
            if (typeof value === 'string' && (operator === 'gte' || operator === 'lte')) {
                backendFilters[key] = formatDateWithTime(value, operator);
            } else {
                backendFilters[key] = value;
            }
            continue;
        }

        // Handle arrays - use 'in' operator
        if (Array.isArray(value)) {
            backendFilters[`${key}_in`] = value;
        }
        // Default: use 'q' for contains/search
        else {
            backendFilters[`${key}_q`] = value;
        }
    }

    return backendFilters;
}

/**
 * Check if a resource should use the /references endpoint
 * 
 * Reference endpoints are lighter, return minimal data, and use smart permission checking.
 * 
 * This function is only used in getList, where we need to distinguish between:
 * - Regular list views (<List>) which use /search (more complete data)
 * - Reference inputs (<ReferenceInput>) which use /references (lighter data)
 * 
 * getMany and getManyReference always use /references since they're always for references.
 * 
 * Our custom ReferenceInput and ReferenceArrayInput components automatically
 * add meta.useReferenceEndpoint flag, so we only need to check for that.
 */
function shouldUseReferenceEndpoint(meta?: { useReferenceEndpoint?: boolean }): boolean {
    return meta?.useReferenceEndpoint === true;
}

/**
 * Extract sort parameters from React Admin SortPayload
 * React Admin always uses { field: string, order: 'ASC' | 'DESC' }
 */
function extractSortParams(sort?: { field: string; order: 'ASC' | 'DESC' }): {
  sortBy?: string;
  sortOrder?: SortDir;
} {
  if (!sort) {
    return {};
  }

  return {
    sortBy: sort.field,
    sortOrder: sort.order === 'ASC' ? SortDir.ASC : SortDir.DESC,
  };
}

export const dataProvider = {
    httpRequest: async (path: string, options: RequestInit = {}) => {
        const { json } = await httpClient(`${API_URL}/${path}`, options);
        return json;
    },

    /**
     * getList is used by:
     * - <List>, <ListGuesser>, <ListBase> - Regular list views (use /search)
     * - <ReferenceInput>, <ReferenceArrayInput> - Dropdowns/autocomplete (use /references via meta.useReferenceEndpoint)
     * - <Count>, <Calendar>, <ExportButton>, etc. - Various components (use /search)
     * 
     * Strategy: Use /references when meta.useReferenceEndpoint is true (set by our custom ReferenceInput components)
     */
    getList: async <RecordType extends RaRecord = RaRecord>(resource: string, params: GetListParams & QueryFunctionContext) => {
        const { page = 1, perPage = 10 } = params.pagination || {};
        const { filter = {} } = params;

        // Special handling for audit resource
        if (resource === 'audit') {
            const { resourceType, resourceId } = filter;
            if (!resourceType || !resourceId) {
                throw new Error('Audit resource requires resourceType and resourceId filters');
            }

            // Extract sort parameters
            const { sortBy, sortOrder } = extractSortParams(params.sort);

            // Build query string for audit endpoint
            const queryParams = new URLSearchParams({
                resourceType: String(resourceType),
                resourceId: String(resourceId),
                page: String(page),
                limit: String(perPage),
            });

            if (sortBy) {
                queryParams.append('sortBy', sortBy);
                queryParams.append('sortOrder', sortOrder || 'DESC');
            }

            const { json } = await httpClient(`${API_URL}/audit?${queryParams.toString()}`, {
                method: 'GET',
            }) as { json: BackendListResponse<RaRecord> };

            return {
                data: json.data as unknown as RaRecord[],
                total: json.totalCount,
            };
        }

        // Resolve virtual resource aliases (e.g. ticket-tag → tag with domain filter)
        const { apiResource, alias } = resolveResource(resource);

        // Extract sort parameters (React Admin always uses SortPayload: { field, order })
        const { sortBy, sortOrder } = extractSortParams(params.sort);

        // Map React Admin filters to our backend format
        const backendFilters = mapReactAdminFiltersToBackend(filter);

        // Determine which endpoint to use based on meta.useReferenceEndpoint flag
        // Our custom ReferenceInput/ReferenceArrayInput components set this automatically
        const useReferenceEndpoint = shouldUseReferenceEndpoint(params.meta);
        const endpoint = useReferenceEndpoint ? `${apiResource}/references` : `${apiResource}/search`;

        // Build request body for POST endpoint
        const requestBody: BackendListRequest = {
            page,
            limit: perPage,
            ...(sortBy && { sortBy }),
            ...(sortOrder && { sortOrder }),
            ...backendFilters,
            ...alias?.listFilters,
        };

        // Call endpoint directly (all CRUDs implement /references)
        const { json } = await httpClient(`${API_URL}/${endpoint}`, {
            method: 'POST',
            body: JSON.stringify(requestBody),
        }) as { json: BackendListResponse<RaRecord> };

        return {
            data: json.data as unknown as RaRecord[],
            total: json.totalCount,
        };
    },

    getOne: async <RecordType extends RaRecord = RaRecord>(resource: string, params: GetOneParams<RecordType> & QueryFunctionContext) => {
        // React Admin calls getOne with id "create" when opening create form
        // Return empty record with id field instead of making a GET request
        if (params.id === 'create') {
            return { data: { id: 'create' } as unknown as RecordType };
        }

        const { apiResource } = resolveResource(resource);
        const { json } = await httpClient(`${API_URL}/${apiResource}/${params.id}`) as { json: BackendResponse<RaRecord> };
        return { data: json.data as unknown as RecordType };
    },

    create: async <RecordType extends Omit<RaRecord, 'id'> = Omit<RaRecord, 'id'>, ResultRecordType extends RaRecord = RecordType & { id: number | string }>(resource: string, params: CreateParams<RecordType>) => {
        const { apiResource, alias } = resolveResource(resource);
        const body = { ...params.data, ...alias?.createData };
        const { json } = await httpClient(`${API_URL}/${apiResource}`, {
            method: 'POST',
            body: JSON.stringify(body),
        }) as { json: BackendResponse<RaRecord> };
        return {
            data: {
                ...params.data,
                ...(json.data as Record<string, unknown>),
                id: (json.data as RaRecord).id
            } as unknown as ResultRecordType
        };
    },

    update: async <RecordType extends RaRecord = RaRecord>(resource: string, params: UpdateParams<RecordType>) => {
        const { apiResource } = resolveResource(resource);
        const { json } = await httpClient(`${API_URL}/${apiResource}/${params.id}`, {
            method: 'PATCH',
            body: JSON.stringify(params.data),
        }) as { json: BackendResponse<RaRecord> };
        return {
            data: {
                ...params.data,
                ...(json.data as Record<string, unknown>),
                id: params.id
            } as unknown as RecordType
        };
    },

    updateMany: async <RecordType extends RaRecord = RaRecord>(resource: string, params: UpdateManyParams<RecordType>) => {
        const { apiResource } = resolveResource(resource);
        const responses = await Promise.all(
            params.ids.map((id: number | string) =>
                httpClient(`${API_URL}/${apiResource}/${id}`, {
                    method: 'PATCH',
                    body: JSON.stringify(params.data),
                }) as Promise<{ json: BackendResponse<RaRecord> }>
            )
        );
        return { data: responses.map(({ json }) => (json.data as RaRecord).id) };
    },

    delete: async <RecordType extends RaRecord = RaRecord>(resource: string, params: DeleteParams<RecordType>) => {
        const { apiResource } = resolveResource(resource);
        await httpClient(`${API_URL}/${apiResource}/${params.id}`, {
            method: 'DELETE',
        });
        return {
            data: params.previousData as unknown as RecordType
        };
    },

    deleteMany: async <RecordType extends RaRecord = RaRecord>(resource: string, params: DeleteManyParams<RecordType>) => {
        const { apiResource } = resolveResource(resource);
        await Promise.all(
            params.ids.map((id: number | string) =>
                httpClient(`${API_URL}/${apiResource}/${id}`, {
                    method: 'DELETE',
                })
            )
        );
        return { data: params.ids };
    },

    /**
     * getMany is used by:
     * - <ReferenceField>, <ReferenceArrayField> - Display referenced records
     * - <ReferenceInput>, <ReferenceArrayInput> - Pre-fill selected values
     * 
     * Strategy: Always use /references with id_in filter (all getMany calls are for references)
     */
    getMany: async <RecordType extends RaRecord = RaRecord>(resource: string, params: GetManyParams<RecordType> & QueryFunctionContext) => {
        if (params.ids.length === 0) {
            return { data: [] };
        }

        const { apiResource, alias } = resolveResource(resource);
        const requestBody: BackendListRequest = {
            page: 1,
            limit: params.ids.length,
            id_in: params.ids,
            ...alias?.listFilters,
        };

        const { json } = await httpClient(`${API_URL}/${apiResource}/references`, {
            method: 'POST',
            body: JSON.stringify(requestBody),
        }) as { json: BackendListResponse<RaRecord> };

        // Return in the same order as requested IDs
        const dataMap = new Map(json.data.map((item: RaRecord) => [item.id, item]));
        return {
            data: params.ids.map((id: number | string) => dataMap.get(id)).filter((item): item is RecordType => item !== undefined) as unknown as RecordType[]
        };
    },

    /**
     * getManyReference is used by:
     * - <ReferenceManyField>, <ReferenceOneField> - Display related records
     * - <ReferenceManyInput>, <ReferenceOneInput> - Edit related records
     * 
     * Strategy: Always use /references endpoint (all getManyReference calls are for references)
     */
    getManyReference: async <RecordType extends RaRecord = RaRecord>(resource: string, params: GetManyReferenceParams & QueryFunctionContext) => {
        const { page = 1, perPage = 10 } = params.pagination;
        const { filter = {} } = params;

        const { apiResource, alias } = resolveResource(resource);

        // Extract sort parameters (React Admin always uses SortPayload: { field, order })
        const { sortBy, sortOrder } = extractSortParams(params.sort);

        // Map React Admin filters to our backend format
        const backendFilters = mapReactAdminFiltersToBackend(filter);

        // Add the reference filter (e.g., { companyId: 123 })
        const referenceField = params.target; // e.g., 'companyId'
        if (referenceField && params.id) {
            backendFilters[`${referenceField}_eq`] = params.id;
        }

        // Build request body for POST endpoint
        const requestBody: BackendListRequest = {
            page,
            limit: perPage,
            ...(sortBy && { sortBy }),
            ...(sortOrder && { sortOrder }),
            ...backendFilters,
            ...alias?.listFilters,
        };

        // Always use /references endpoint (all getManyReference calls are for references)
        const { json } = await httpClient(`${API_URL}/${apiResource}/references`, {
            method: 'POST',
            body: JSON.stringify(requestBody),
        }) as { json: BackendListResponse<RaRecord> };

        return {
            data: json.data as unknown as RaRecord[],
            total: json.totalCount,
        };
    },

    // Custom methods for non-CRUD operations
    // Usage: const dataProvider = useDataProvider(); dataProvider.ticketListByTab({ tab: 'attending' });
    ...customDataProviderMethods,
};
